<template>
  <!-- El marco es el Dialog de PrimeVue (rol, aria-modal, «atrás»); el overlay, la tarjeta y su
       gf-fade son los de siempre, y el marco espera a que el fundido de salida termine. Escape lo
       sigue llevando la tarjeta (onKeydown); el «atrás» de Android hace lo mismo (onFrameBack). -->
  <BzDialog :visible="visible" band="helpFlow" transition="gf-fade" :labelled-by="titleId" :escape="false" @dismiss="onFrameBack">
    <div
      class="gf-overlay"
      :class="{ 'is-view': step === 'view' }"
      :data-flow="flowId"
      data-testid="guided-flow"
      @click.self="step !== 'working' && $emit('dismiss', 'outside')"
    >
      <section
        ref="card"
        class="gf-card"
        data-testid="guided-flow-card"
        tabindex="-1"
        :data-step="step"
        @keydown="onKeydown"
      >
        <!-- Campo invisible que recibe el foco EN el toque de «Otra cosa»: Android e iOS solo abren el
             teclado con un foco dentro del gesto, y el campo de verdad aún no existe (llega tras la
             transición). Con el teclado ya arriba, pasar el foco al campo de verdad lo mantiene. -->
        <input
          ref="keyboardProxy"
          class="gf-keyboard-proxy"
          type="text"
          tabindex="-1"
          :aria-label="$t('message.helpFlowComposeTitle')"
          autocomplete="off"
        />
        <!-- `mode="out-in"`: el paso nuevo no está en el DOM hasta que el viejo ha salido, así
             que el foco del campo de texto se pide en after-enter y no en el watcher del paso. -->
        <transition name="gf-step" mode="out-in" @after-enter="onStepEntered">
          <!-- CHOOSE: the topic grid. One tap says what is wrong; the hint says what happens next. -->
          <div v-if="step === 'choose'" key="choose" class="gf-step">
            <header class="gf-head">
              <h1 :id="titleId" class="gf-title">{{ $t(titleKey) }}</h1>
              <p v-if="subtitleKey" class="gf-subtitle">{{ $t(subtitleKey) }}</p>
            </header>
            <div class="gf-grid gf-grid--topics">
              <!-- A topic is the manual order's tile (Adrián, 13-oct-2026: the two menus ask the same
                   thing, «pick one»): its mark on a square of 48 above, its words below, read at a metre
                   (`--far`). The exits (free text, a voice note) are the system's tile in its row shape
                   with only the outline, so they never pass for a topic. -->
              <button
                v-for="option in options"
                :key="option.id"
                type="button"
                class="bz-tile gf-option"
                :class="{
                  'bz-tile--far gf-topic': !isExit(option),
                  'bz-tile--row is-free': isExit(option),
                  'is-half': isExit(option) && pairedExits,
                }"
                :data-testid="`guided-flow-option-${option.id}`"
                @click="onChoose(option)"
              >
                <span v-if="!isExit(option)" class="gf-mark" aria-hidden="true">
                  <svg v-if="option.iconPath" class="bz-tile__icon gf-glyph" viewBox="0 0 24 24" fill="currentColor" shape-rendering="geometricPrecision">
                    <path :d="option.iconPath" />
                  </svg>
                  <BzIcon v-else-if="option.icon" :name="option.icon" class="bz-tile__icon" />
                </span>
                <template v-else>
                  <svg v-if="option.iconPath" class="bz-tile__icon gf-glyph" viewBox="0 0 24 24" fill="currentColor" shape-rendering="geometricPrecision" aria-hidden="true">
                    <path :d="option.iconPath" />
                  </svg>
                  <BzIcon v-else-if="option.icon" :name="option.icon" class="bz-tile__icon" />
                </template>
                <span class="gf-option-copy">
                  <span class="bz-tile__label">{{ $t(option.labelKey) }}</span>
                  <small v-if="option.hintKey">{{ $t(option.hintKey) }}</small>
                </span>
              </button>
            </div>
          </div>

          <!-- PICK: which order. Only reached when the topic is about an order and none is open. -->
          <div v-else-if="step === 'pick'" key="pick" class="gf-step">
            <header class="gf-head gf-head--sub">
              <BzButton
                class="gf-back"
                kind="secondary"
                icon="arrow_back"
                :aria-label="$t('message.guidedFlowBack')"
                data-testid="guided-flow-back"
                @click="$emit('back')"
              />
              <div class="gf-head-copy">
                <h1 :id="titleId" class="gf-title gf-title--sub">{{ pickTitleKey ? $t(pickTitleKey) : '' }}</h1>
                <p v-if="pickHintKey" class="gf-subtitle">{{ $t(pickHintKey) }}</p>
              </div>
            </header>
            <!-- A list the host has to fetch (orders that already left the screen) has three more
                 states than one read from the screen: on its way, failed, and empty. -->
            <div v-if="pickStatus === 'loading'" class="bz-empty gf-pick-state" role="status" aria-live="polite" data-testid="guided-flow-pick-loading">
              <BzIcon name="progress_activity" class="bz-empty__icon bz-spinner" />
              <p v-if="pickLoadingKey" class="bz-empty__title">{{ $t(pickLoadingKey) }}</p>
            </div>
            <div v-else-if="pickStatus === 'failed' || pickStatus === 'offline'" class="bz-empty gf-pick-state" data-testid="guided-flow-pick-error">
              <BzIcon :name="pickStatus === 'offline' ? 'wifi_off' : 'warning'" class="bz-empty__icon" />
              <p class="bz-empty__title">
                {{ pickStatus === 'offline' ? $t('message.orderPhotosOffline') : (pickFailedKey ? $t(pickFailedKey) : '') }}
              </p>
              <p v-if="pickStatus === 'offline'" class="bz-empty__text">{{ $t('message.guidedFlowUnavailableOffline') }}</p>
              <div class="gf-answers">
                <BzButton
                  kind="primary"
                  size="lg"
                  icon="refresh"
                  :label="$t('message.guidedFlowRetry')"
                  data-testid="guided-flow-pick-retry"
                  @click="$emit('refresh')"
                />
                <BzButton
                  kind="secondary"
                  size="lg"
                  :label="$t(pickFallbackKey || pickNoneKey || 'message.guidedFlowNo')"
                  data-testid="guided-flow-pick-fallback"
                  @click="$emit('pick', null)"
                />
              </div>
            </div>
            <div v-else class="gf-grid gf-grid--pick" :class="{ 'gf-grid--pick-2': pickTwoColumns }">
              <div v-if="!pickItems.length && pickEmptyKey" class="bz-empty gf-pick-empty" data-testid="guided-flow-pick-empty">
                <BzIcon name="image" class="bz-empty__icon" />
                <p class="bz-empty__title">{{ $t(pickEmptyKey) }}</p>
              </div>
              <button
                v-for="item in pickItems"
                :key="item.id"
                type="button"
                class="bz-tile bz-tile--row gf-pick"
                :class="{ 'gf-pick--multi': multi, 'is-selected': multi && selected.includes(item.id) }"
                :aria-pressed="multi ? String(selected.includes(item.id)) : null"
                :data-testid="`guided-flow-pick-${item.id}`"
                @click="onPick(item)"
              >
                <span v-if="multi" class="gf-check" aria-hidden="true">
                  <BzIcon v-if="selected.includes(item.id)" name="check" />
                </span>
                <img v-if="item.icon" :src="item.icon" alt="" class="gf-pick-icon" />
                <span v-else class="gf-pick-icon gf-pick-icon--blank" aria-hidden="true"></span>
                <span class="gf-pick-copy">
                  <strong class="gf-pick-code" translate="no">{{ item.label }}</strong>
                  <small v-if="item.sublabel">{{ item.sublabel }}</small>
                </span>
                <span v-if="item.badgeKey" class="bz-chip gf-badge" :class="chipFor(item.badgeTone, item.badgeKey).cls">
                  <BzIcon :name="chipFor(item.badgeTone, item.badgeKey).icon" />
                  {{ $t(item.badgeKey) }}
                </span>
              </button>
              <!-- Older orders, fetched on demand: side by side with "not in the list" so the pair
                   costs one row, not two, in the 614 px of the pass tablet. -->
              <button
                v-if="pickMore !== 'none'"
                type="button"
                class="bz-tile bz-tile--row gf-option is-free is-half"
                :disabled="pickMore === 'loading'"
                data-testid="guided-flow-pick-more"
                @click="$emit('more')"
              >
                <BzIcon :name="pickMore === 'loading' ? 'progress_activity' : 'history'" class="bz-tile__icon" :class="{ 'bz-spinner': pickMore === 'loading' }" />
                <span class="gf-option-copy"><span class="bz-tile__label">{{ pickMoreKey ? $t(pickMoreKey) : '' }}</span></span>
              </button>
              <button
                type="button"
                class="bz-tile bz-tile--row gf-option is-free"
                :class="{ 'is-half': pickMore !== 'none' }"
                data-testid="guided-flow-pick-none"
                @click="$emit('pick', null)"
              >
                <BzIcon name="question_mark" class="bz-tile__icon" />
                <span class="gf-option-copy"><span class="bz-tile__label">{{ pickNoneKey ? $t(pickNoneKey) : '' }}</span></span>
              </button>
            </div>
            <!-- Multiselección: la elección no se cierra al primer toque, así que hace falta un
                 "ya está". Deshabilitado mientras no haya nada marcado: enviar una lista vacía
                 sería un tique que no dice nada. -->
            <div v-if="multi" class="gf-pick-actions">
              <BzButton
                kind="primary"
                size="lg"
                icon="send"
                :label="$t('message.helpFlowComposeSend')"
                :disabled="selected.length === 0"
                data-testid="guided-flow-pick-send"
                @click="sendSelection"
              />
            </div>
          </div>

          <!-- COMPOSE: texto libre en NUESTRO campo, que abre el teclado de la tablet (dentro del
               iframe de Intercom no se puede). Volver, el campo y Enviar en UNA fila pegada arriba:
               con el teclado abierto en la Honor quedan ~174 px visibles de 518, y el teclado tapa
               desde abajo, así que solo lo que está arriba y es bajo queda a la vista (v4.60.2,
               7-oct-2026; en el sistema de diseño desde el 15-oct). -->
          <div v-else-if="step === 'compose'" key="compose" class="gf-step gf-compose">
            <h1 :id="titleId" class="gf-visually-hidden">{{ $t('message.helpFlowComposeTitle') }}</h1>
            <BzButton
              class="gf-back"
              kind="secondary"
              icon="arrow_back"
              :aria-label="$t('message.guidedFlowBack')"
              data-testid="guided-flow-back"
              @click="$emit('back')"
            />
            <textarea
              ref="composer"
              v-model="draft"
              class="gf-composer"
              rows="2"
              data-testid="guided-flow-composer"
              :aria-labelledby="titleId"
              :placeholder="$t('message.helpFlowComposePlaceholder')"
              enterkeyhint="send"
              @keydown.enter.exact.prevent="sendDraft"
            ></textarea>
            <BzButton
              class="gf-send"
              kind="primary"
              size="lg"
              icon="send"
              :label="$t('message.helpFlowComposeSend')"
              :disabled="!draft.trim()"
              data-testid="guided-flow-compose-send"
              @click="sendDraft"
            />
          </div>

          <!-- CODE: the order is not in the list, so its code, typed — the question support would
               otherwise ask back. One field and one way on; «No lo tengo» goes on without it,
               because a code nobody has at hand is no reason to leave the kitchen without help. -->
          <div v-else-if="step === 'code'" key="code" class="gf-step">
            <header class="gf-head gf-head--sub">
              <BzButton
                class="gf-back"
                kind="secondary"
                icon="arrow_back"
                :aria-label="$t('message.guidedFlowBack')"
                data-testid="guided-flow-back"
                @click="$emit('back')"
              />
              <div class="gf-head-copy">
                <h1 :id="titleId" class="gf-title gf-title--sub">{{ $t('message.helpFlowCodeTitle') }}</h1>
                <p class="gf-subtitle">{{ $t('message.helpFlowCodeHint') }}</p>
              </div>
            </header>
            <InputText
              ref="codeField"
              v-model="codeDraft"
              class="gf-composer gf-code"
              data-testid="guided-flow-code"
              :aria-labelledby="titleId"
              :placeholder="$t('message.helpFlowCodePlaceholder')"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              enterkeyhint="send"
              @keydown.enter.exact.prevent="sendCode"
            />
            <div class="gf-answers">
              <BzButton
                kind="primary"
                size="lg"
                icon="send"
                :label="$t('message.helpFlowComposeSend')"
                :disabled="!codeDraft.trim()"
                data-testid="guided-flow-code-send"
                @click="sendCode"
              />
              <BzButton
                kind="secondary"
                size="lg"
                :label="$t('message.helpFlowCodeSkip')"
                data-testid="guided-flow-code-skip"
                @click="$emit('submit-code', null)"
              />
            </div>
          </div>

          <!-- WORKING: a remedy is running. No close button — the page is about to reload. The system's
               spinner says it is still working; the title says what. -->
          <div v-else-if="step === 'working'" key="working" class="gf-step gf-step--center" role="status" aria-live="polite">
            <BzIcon name="progress_activity" class="gf-wait-icon bz-spinner" />
            <h1 :id="titleId" class="gf-title">{{ workingKey ? $t(workingKey) : '' }}</h1>
            <p v-if="workingSubKey" class="gf-subtitle gf-subtitle--center">{{ $t(workingSubKey) }}</p>
          </div>

          <!-- CONFIRM: did that fix it? Two equal answers — both are legitimate. -->
          <div v-else-if="step === 'confirm'" key="confirm" class="gf-step gf-step--center">
            <p v-if="confirmSubKey" class="gf-kicker">{{ $t(confirmSubKey) }}</p>
            <h1 :id="titleId" class="gf-title gf-title--question">{{ confirmKey ? $t(confirmKey) : '' }}</h1>
            <p v-if="evidence" class="bz-chip bz-chip--warn gf-evidence" data-testid="guided-flow-evidence">
              <BzIcon name="info" />
              <span>{{ evidence }}</span>
            </p>
            <div class="gf-answers">
              <BzButton
                kind="primary"
                size="lg"
                icon="check"
                :label="$t('message.guidedFlowYes')"
                data-testid="guided-flow-yes"
                @click="$emit('answer', true)"
              />
              <BzButton
                kind="secondary"
                size="lg"
                :label="$t('message.guidedFlowNo')"
                data-testid="guided-flow-no"
                @click="$emit('answer', false)"
              />
            </div>
          </div>

          <!-- VIEW: the picked subject itself — for help, an order's photos, on the whole screen. The
               bar names the order the way its tile did (code, platform, when, verdict) so the kitchen
               knows it opened the right one; support is one quiet tap away, never the default. -->
          <div v-else-if="step === 'view'" key="view" class="gf-step gf-step--view">
            <header class="gf-head gf-head--sub gf-head--view">
              <BzButton
                class="gf-back"
                kind="secondary"
                icon="arrow_back"
                :aria-label="$t('message.guidedFlowBack')"
                data-testid="guided-flow-back"
                @click="$emit('back')"
              />
              <div class="gf-view-head">
                <img v-if="viewItem && viewItem.icon" :src="viewItem.icon" alt="" class="gf-pick-icon" />
                <div class="gf-head-copy">
                  <h1 :id="titleId" class="gf-title gf-title--sub gf-view-code" data-testid="guided-flow-view-code" translate="no">
                    {{ viewItem ? viewItem.label : '' }}
                  </h1>
                  <p v-if="viewItem && viewItem.sublabel" class="gf-view-sub">{{ viewItem.sublabel }}</p>
                </div>
                <span v-if="viewItem && viewItem.badgeKey" class="bz-chip gf-badge" :class="chipFor(viewItem.badgeTone, viewItem.badgeKey).cls">
                  <BzIcon :name="chipFor(viewItem.badgeTone, viewItem.badgeKey).icon" />
                  {{ $t(viewItem.badgeKey) }}
                </span>
              </div>
            </header>
            <OrderPhotosViewer
              :photos="viewPhotos"
              :status="viewStatus"
              :code="viewItem ? String(viewItem.label || '') : ''"
              @retry="$emit('refresh-view')"
              @browse="$emit('back')"
              @photo-load="$emit('photo-load', $event)"
            />
            <div v-if="viewSupportKey" class="gf-view-foot">
              <BzButton
                kind="outlined"
                size="sm"
                icon="forum"
                :label="$t(viewSupportKey)"
                data-testid="guided-flow-view-support"
                @click="$emit('view-support')"
              />
            </div>
          </div>

          <!-- UNAVAILABLE: wanted to write to support, but the messenger cannot be reached. -->
          <div v-else-if="step === 'unavailable'" key="unavailable" class="gf-step gf-step--center">
            <span class="gf-unavailable-icon" aria-hidden="true">
              <BzIcon :name="unavailableReason === 'offline' ? 'wifi_off' : 'warning'" />
            </span>
            <h1 :id="titleId" class="gf-title gf-title--question">{{ $t('message.guidedFlowUnavailableTitle') }}</h1>
            <p class="gf-subtitle gf-subtitle--center">
              {{ $t(unavailableReason === 'offline' ? 'message.guidedFlowUnavailableOffline' : 'message.guidedFlowUnavailableMessenger') }}
            </p>
            <div class="gf-answers">
              <BzButton
                kind="primary"
                size="lg"
                icon="refresh"
                :label="$t('message.guidedFlowRetry')"
                data-testid="guided-flow-retry"
                @click="$emit('retry')"
              />
              <BzButton
                kind="secondary"
                size="lg"
                :label="$t('message.guidedFlowReloadApp')"
                data-testid="guided-flow-reload"
                @click="$emit('reload')"
              />
            </div>
            <BzButton
              class="gf-back--inline"
              kind="text"
              size="sm"
              icon="arrow_back"
              :label="$t('message.guidedFlowBack')"
              @click="$emit('back')"
            />
          </div>
        </transition>

        <BzButton
          v-if="step !== 'working'"
          class="gf-close"
          kind="secondary"
          icon="close"
          :aria-label="$t('message.close')"
          data-testid="guided-flow-close"
          @click="$emit('dismiss', 'button')"
        />
      </section>
    </div>
  </BzDialog>
</template>

<script>
/**
 * GuidedFlowModal — the presentation of a guided flow (see src/guidedFlow.js for the engine).
 *
 * Purely presentational: it renders whichever `step` the host says the flow is on and emits the
 * person's taps. It owns no flow state, so the same component serves the help flow today and the
 * "AI wrong" flow next — the host swaps the definition (options, copy keys) and the engine.
 *
 * Everything on it is sized for a kitchen tablet read at arm's length and tapped with gloves:
 * targets ≥ 76 px tall, one decision per screen, no paragraphs. Tokens are the ones already used
 * by PhotoCoverageAlertComponent so both surfaces read as one family.
 */
import OrderPhotosViewer from './OrderPhotosViewer.vue';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

import InputText from 'primevue/inputtext';
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';
import BzIcon from '@/ui/components/BzIcon.vue';

// A state of an order, in the system's tag (norm 11: the colour, with its icon and its word). `badgeTone` is
// the colour the order wears in the kitchen's list: green correct, orange incorrect, blue a photo with no
// verdict yet (or none), slate the same, waiting for a connection.
const CHIPS = {
  green: { cls: 'bz-chip--ok', icon: 'check_circle' },
  orange: { cls: 'bz-chip--wrong', icon: 'error' },
  red: { cls: 'bz-chip--wrong', icon: 'error' },
  amber: { cls: 'bz-chip--warn', icon: 'warning' },
  blue: { cls: 'bz-chip--pending', icon: 'schedule' },
  slate: { cls: 'bz-chip--pending', icon: 'schedule' },
};

const NO_PHOTO_BADGE = 'message.helpFlowBadgeNoPhoto';

// Steps whose Escape, whose ← button and Android's back go one level back instead of closing the flow.
const STEPS_WITH_BACK = ['pick', 'unavailable', 'compose', 'view', 'code'];

export default {
  name: 'GuidedFlowModal',
  components: { BzButton, BzDialog, BzIcon, OrderPhotosViewer, InputText },
  props: {
    flowId: { type: String, required: true },
    visible: { type: Boolean, default: false },
    /** One of the engine's STEP values; null while closed. */
    step: { type: String, default: null },
    /**
     * Topic definitions: `{ id, iconPath | icon, labelKey, hintKey, free, voice }`. `iconPath` is a filled
     * 24-grid silhouette; `icon` is the name of a Material Symbol (src/ui/icons.js), the fallback.
     */
    options: { type: Array, default: () => [] },
    /** Subjects for the PICK step: `{ id, label, sublabel, icon, badgeKey, badgeTone }`. */
    pickItems: { type: Array, default: () => [] },
    titleKey: { type: String, required: true },
    subtitleKey: { type: String, default: null },
    pickTitleKey: { type: String, default: null },
    pickHintKey: { type: String, default: null },
    pickNoneKey: { type: String, default: null },
    /** La respuesta del paso PICK es una lista, no un elemento. */
    multi: { type: Boolean, default: false },
    workingKey: { type: String, default: null },
    /** Segunda línea del paso de remedio: lo que va a tardar y qué pasa después. */
    workingSubKey: { type: String, default: null },
    confirmKey: { type: String, default: null },
    confirmSubKey: { type: String, default: null },
    /** Already-translated fact shown under the CONFIRM question. */
    evidence: { type: String, default: null },
    /** 'offline' | 'messenger' — picks the UNAVAILABLE copy. */
    unavailableReason: { type: String, default: null },
    /**
     * A PICK list the host fetches: 'ready' | 'loading' | 'failed' | 'offline'. Lists read from
     * the screen never leave 'ready', so every other topic renders exactly as before.
     */
    pickStatus: { type: String, default: 'ready' },
    pickLoadingKey: { type: String, default: null },
    pickFailedKey: { type: String, default: null },
    /** The way out of a failed list (for photos: ask support for them). */
    pickFallbackKey: { type: String, default: null },
    /** Shown when a fetched list came back empty. */
    pickEmptyKey: { type: String, default: null },
    /** 'none' | 'available' | 'loading' — the "older ones" button of a paged list. */
    pickMore: { type: String, default: 'none' },
    /** Two columns down to 900 px wide: a page of eight fits the pass tablet without scrolling. */
    pickTwoColumns: { type: Boolean, default: false },
    pickMoreKey: { type: String, default: null },
    /** VIEW step: the subject's header, shaped like a pick item (`label`, `sublabel`, `icon`, badge). */
    viewItem: { type: Object, default: null },
    /** VIEW step: `[{ url, timeLabel }]`, oldest first. */
    viewPhotos: { type: Array, default: () => [] },
    /** VIEW step: 'loading' | 'ready' | 'failed' | 'offline'. */
    viewStatus: { type: String, default: 'ready' },
    /** VIEW step: the quiet "write to support about this" link; null hides it. */
    viewSupportKey: { type: String, default: null },
  },
  emits: [
    'choose', 'pick', 'answer', 'retry', 'reload', 'back', 'dismiss', 'submit-text', 'submit-code',
    'refresh', 'more', 'view-support', 'refresh-view', 'photo-load',
  ],
  data() {
    return {
      selected: [],  // ids marcados en una multiselección
      draft: '',     // texto libre en curso
      codeDraft: '', // el código del pedido que no estaba en la lista
    };
  },
  computed: {
    titleId() {
      return `guided-flow-title-${this.flowId}`;
    },
    // Dos salidas (escribirlo o decirlo en una nota de voz) comparten la última fila: la de
    // siempre a ancho completo costaría una fila más, y en los 614 px de alto de la tablet del
    // pase no sobra ninguna.
    pairedExits() {
      return this.options.filter((option) => this.isExit(option)).length === 2;
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.selected = [];
        this.draft = '';
        this.codeDraft = '';
        this.focusDialog();
      }
    },
    step(next) {
      if (!this.visible) return;
      // Each step starts at its top. The card scrolls on small screens, and without this the new
      // step inherited the old one's offset: on a phone, tapping a topic low in the grid opened the
      // next step with its title and its ← scrolled out of sight.
      if (this.$refs.card) this.$refs.card.scrollTop = 0;
      // Volver a la parrilla descarta lo a medias: una selección de otro tema no se arrastra.
      if (next === 'choose') { this.selected = []; this.draft = ''; this.codeDraft = ''; }
      // El foco del campo de texto lo pide onStepEntered, cuando el elemento existe de verdad.
      if (next !== 'compose' && next !== 'code') this.focusDialog();
    },
  },
  methods: {
    // «Sin foto» is the one pending state a clock says wrong: no photo is not a photo being analysed.
    chipFor(tone, badgeKey) {
      const chip = CHIPS[tone] || CHIPS.slate;
      return badgeKey === NO_PHOTO_BADGE ? { ...chip, icon: 'no_photography' } : chip;
    },
    // Una salida (texto libre o nota de voz) no es un tema: va aparte, al final y más callada.
    isExit(option) {
      return Boolean(option && (option.free || option.voice));
    },
    onChoose(option) {
      // Texto libre: el teclado se pide aquí, dentro del toque (ver gf-keyboard-proxy).
      if (option && option.free) {
        const proxy = this.$refs.keyboardProxy;
        if (proxy) proxy.focus({ preventScroll: true });
      }
      this.$emit('choose', option);
    },
    focusables() {
      const card = this.$refs.card;
      return card ? Array.from(card.querySelectorAll(FOCUSABLE)) : [];
    },
    // Focus lands on the dialog itself, not on a button: the screen reader announces the step's
    // title, Tab reaches the first choice — and no answer is pre-highlighted. (After a remedy
    // reload nothing has been touched yet, so a focused "Sí" would wear a focus ring and read as
    // the recommended answer; both answers are legitimate.)
    focusDialog() {
      this.$nextTick(() => {
        const card = this.$refs.card;
        if (card) card.focus({ preventScroll: true });
      });
    },
    onPick(item) {
      if (!this.multi) { this.$emit('pick', item); return; }
      const i = this.selected.indexOf(item.id);
      if (i === -1) this.selected = [...this.selected, item.id];
      else this.selected = this.selected.filter((id) => id !== item.id);
    },
    sendSelection() {
      if (!this.selected.length) return;
      // Se devuelven los ITEMS en el orden en que se pintan, no en el que se tocaron: el mensaje
      // se lee mejor en el mismo orden que la pantalla.
      this.$emit('pick', this.pickItems.filter((item) => this.selected.includes(item.id)));
    },
    sendDraft() {
      const text = this.draft.trim();
      if (!text) return;
      this.$emit('submit-text', text);
    },
    sendCode() {
      const code = this.codeDraft.trim();
      if (!code) return;
      this.$emit('submit-code', code);
    },
    onStepEntered() {
      if (this.step === 'compose') this.focusComposer();
      // The field, so the keyboard comes up: the code is the one thing this step asks for.
      if (this.step === 'code') {
        this.$nextTick(() => {
          const el = this.$refs.codeField?.$el;
          if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
        });
      }
    },
    focusComposer() {
      this.$nextTick(() => {
        const el = this.$refs.composer;
        if (el) el.focus({ preventScroll: true });
      });
    },
    // Escape and Android's back walk the same way out: a step back where there is one, the flow
    // closed where there is not, and nothing while it works (the page is reloading).
    stepBack(origin) {
      if (this.step === 'working') return;                        // nothing to cancel: the page is reloading
      if (STEPS_WITH_BACK.includes(this.step)) this.$emit('back');
      else this.$emit('dismiss', origin);
    },
    onFrameBack() {
      this.stepBack('back');
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.stepBack('button');
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep the tab cycle inside the dialog while it is up.
      const items = this.focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const onDialog = document.activeElement === this.$refs.card;
      if (event.shiftKey && (document.activeElement === first || onDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
/* On the design system (docs/sistema-diseno/README.md, «Ayuda»): only its tokens, its tile (`.bz-tile`),
   its tag (`.bz-chip`) and its empty state (`.bz-empty`), and the buttons of src/ui/components/. The
   diagonal light on the edges (gradients, norm 11's debt), the amber plates under the icons and the
   300-red and -green of the state chips are gone: the state is the palette's, with its icon and its
   word, never the colour alone (stage 2, 8-oct-2026). */
.gf-overlay {
  position: fixed;
  inset: 0;
  /* Above the coverage banner (9600): asking for help outranks being nudged. Below the
     blocking notification (9999) and the rider toasts (10000): those are the kitchen's
     real-time reality and must never be hidden by a form. */
  z-index: var(--bz-z-help-flow);
  display: grid;
  place-items: center;
  /* One row as tall as the overlay, so the card's `max-height: 100%` is the height really on
     screen. It used to be `100vh`, which iOS Safari and Chrome ≥108 measure WITHOUT their toolbars:
     on a phone the card came out taller than what is visible, was centred anyway and overflowed
     both ways, and its top — the title, the ✕ — sat above the screen, out of reach of any scroll
     (found on an iPhone, 2026-09-30). On a desktop the two are the same number: nothing moves. */
  grid-template-rows: minmax(0, 1fr);
  padding: var(--bz-space-4);
  /* The notch and the home bar, where the app runs full screen (viewport-fit=cover). */
  padding: max(var(--bz-space-4), env(safe-area-inset-top)) max(var(--bz-space-4), env(safe-area-inset-right))
           max(var(--bz-space-4), env(safe-area-inset-bottom)) max(var(--bz-space-4), env(safe-area-inset-left));
  box-sizing: border-box;
  /* Opaque on purpose: while help is asked for, the order list and the camera behind are not context,
     they are noise that competes for the eye. The app's own background, flat: no `backdrop-filter`,
     the blur costs the €100 tablets and adds nothing that the opacity does not. */
  background: var(--bz-bg);
  font-family: var(--bz-font);
  font-synthesis: none;
  font-kerning: normal;
}

/* The photos take the whole screen: no card around them, the app's background, only the safe areas
   kept. The card below turns into the screen itself. */
.gf-overlay.is-view {
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
}

.gf-card {
  position: relative;
  box-sizing: border-box;
  width: min(1240px, 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: var(--bz-space-7) var(--bz-space-8) var(--bz-space-8);
  border-radius: var(--bz-radius-xl);
  border: 1px solid var(--bz-edge);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
}

/* Los pasos de una sola frase no necesitan el ancho de la parrilla: a 1240 px la pregunta
   quedaba flotando en una banda vacía y la mirada tenía que buscarla. Se estrechan a la medida
   de su contenido, que además los distingue de un vistazo del paso de elección. */
.gf-card[data-step="working"],
.gf-card[data-step="confirm"],
.gf-card[data-step="unavailable"] {
  width: min(760px, 100%);
}

/* The dialog takes focus on open (see focusDialog); it must not draw a ring for it. */
.gf-card:focus,
.gf-card:focus-visible { outline: none; }

.gf-step { min-height: 0; }

/* ---- header ---- */
/* 32 px bajo la pregunta: el aire del sistema entre dos grupos (space-7, el mismo que separa los de
   Ajustes). La jerarquía no la sostiene solo el tamaño: pegada a la primera baldosa, la pregunta se
   leía como la cabecera de la lista y no como lo que hay que contestar. */
.gf-head {
  margin: 0 calc(var(--bz-target-min) + var(--bz-space-3)) var(--bz-space-7) 0; /* the right margin keeps the title clear of the ✕ */
}

.gf-head--sub {
  display: flex;
  align-items: center;
  gap: var(--bz-space-4);
}

.gf-head-copy { min-width: 0; }

/* El título de una pantalla del sistema: 28/36 a 600, el tope de la escala (14, 18, 22, 28). Manda
   por tamaño; las opciones, a 24, siguen siendo lo que la mirada elige. El tracking, el de DM Sans en
   su tamaño óptico (0). */
.gf-title {
  margin: 0;
  font-size: var(--bz-text-3xl);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  color: var(--bz-ink);
  text-wrap: balance;
}

/* El título de un paso: el de un diálogo del sistema, 22/28. */
.gf-title--sub {
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
}

.gf-subtitle {
  margin: var(--bz-space-2) 0 0;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-md);
  text-wrap: pretty;
}

.gf-subtitle--center {
  max-width: 640px;
  text-align: center;
}

/* ---- the system's tile, here ---- */
.gf-option,
.gf-pick {
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
}

.gf-option:focus-visible,
.gf-pick:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

/* ---- the topic grid ---- */
/* Tres temas por fila en la tablet, como las plataformas del pedido manual van de dos en dos en su
   diálogo estrecho: con el icono encima, la etiqueta tiene todo el ancho de la baldosa (342 px en los
   1160 útiles de una de 1280), y la frase más larga en francés cabe en dos líneas a 24 px. La fila crece
   con la más alta, así que una de dos líneas no deja a sus vecinas desiguales. Seis pistas y no tres: un
   tema ocupa dos y cada una de las dos salidas tres, así que las salidas llenan su fila a medias, sin
   dejar un tercio vacío. Más estrecho que 1024, dos temas por fila; en el móvil, uno, con el icono al
   lado (las reglas de abajo). */
.gf-grid {
  display: grid;
  gap: var(--bz-space-4);
}

.gf-grid--topics {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.gf-grid--topics .gf-topic { grid-column: span 2; }
.gf-grid--topics .gf-option.is-free { grid-column: 1 / -1; }
.gf-grid--topics .gf-option.is-free.is-half { grid-column: span 3; }

/* The topic: the system's tile, stacked like the manual order's platforms — its mark on top, its words
   below — at the radius, fill and edge of the cards, flat, no `:hover` (Android leaves it lit). */
.gf-topic {
  justify-content: flex-start;
  gap: var(--bz-space-4);
  padding: var(--bz-space-4) var(--bz-space-5) var(--bz-space-5);
}

/* The mark: the square of 48 the manual order's logos sit on, in the selection's blue with the icon in
   the action blue, the pair the tiles already use — the topic's own colour, the way a platform's logo is. */
.gf-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  border-radius: var(--bz-radius-md);
  background: var(--bz-action-container);
}

/* Every option: at least 96 high (the target of the action a screen exists for). The hairline is
   deliberate (1,39:1 on the card): the tile is recognised by its fill, its icon and its text, like a
   grouped row. The system's outline is for what has only an edge. */
.gf-option {
  text-align: left;
}

/* The silhouette of a topic (helpFlow.js `iconPath`): a filled 24-grid path, so it is drawn at the
   tile's icon size, not as a glyph of a font. */
.gf-glyph {
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
}

/* The exit — free text, a voice note, "not in the list" — is not a topic: quieter, full width, last,
   and 16 px further down, which reads it as "none of the above" and not as a seventh. No fill and the
   system's outline, the one of the secondary button (outline, 4,44:1 on the card): it belongs to the
   family without competing with the six. */
.gf-option.is-free {
  grid-column: 1 / -1;
  min-height: var(--bz-target-primary);
  margin-top: var(--bz-space-4);
  padding: var(--bz-space-3) var(--bz-space-5);
  border-color: var(--bz-outline);
  background: transparent;
  color: var(--bz-ink-2);
}

.gf-option.is-free .bz-tile__icon {
  font-size: var(--bz-icon-md);
  color: var(--bz-ink-2);
}

.gf-option.is-free .gf-glyph {
  width: var(--bz-icon-md);
  height: var(--bz-icon-md);
}

.gf-option-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-1);
}

/* The label is the tile's `--far` one: 22 at 600, the label of a 96 button (it was 24 at 500, measured
   on 6-oct to read at 14 minutes of arc from half a metre; 22 is 12,9 there, and the step of 24 was
   folded on 13-oct so each role has one size). The French's longest phrase stays in two lines.
   `opsz 12` is the lever that really changes legibility here: DM Sans is variable and with
   `font-optical-sizing: auto` the browser hands it the size in pixels — the display design, tighter and
   with a smaller x-height. What this label is read at is the angular size of 10 or 11 px at thirty
   centimetres, so it takes the text instance: more spacing and a bigger x-height, free.
   `balance` does not change the number of lines, it shares the two so no orphan word is left below;
   `anywhere` is only a net: the longest word of the catalog is 137 px wide. */
.gf-option .bz-tile__label {
  font-variation-settings: "opsz" 12;
  text-wrap: balance;
  overflow-wrap: anywhere;
  hyphens: manual;
}

/* The exit's label: a default tile's 18 at 600, a step under the topics', in the second ink. */
.gf-option.is-free .bz-tile__label {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  color: var(--bz-ink-2);
}

.gf-option-copy small {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
}

.gf-option:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ---- the pick list ---- */
.gf-grid--pick { grid-template-columns: repeat(2, minmax(0, 1fr)); }

/* An order: the system's tile in a row, its platform, its code and, at the end, its state. */
.gf-pick {
  display: grid;
  grid-template-columns: var(--bz-target-min) minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--bz-space-4);
  min-height: var(--bz-target-primary);
  padding: var(--bz-space-3) var(--bz-space-4);
  text-align: left;
}

.gf-pick-icon {
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  border-radius: var(--bz-radius-md);
  object-fit: contain;
}

.gf-pick-icon--blank { background: var(--bz-edge); }

.gf-pick-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* The code is what the kitchen matches against the ticket: the tile's biggest, at the weight of a code. */
.gf-pick-code {
  font-size: var(--bz-text-3xl);
  font-weight: var(--bz-weight-strong);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gf-pick-copy small {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
}

/* The state of an order is the system's tag — the palette's, with its icon and its word (norm 11): the
   same colours the list the kitchen is looking at wears, correct green and incorrect bronze. */
.gf-badge { flex: 0 0 auto; }

/* Solo en multiselección se añade la columna del check. Declararla siempre dejaba el nombre en
   la columna del icono cuando no había check, y se veía "G…" en vez de "Glovo". */
.gf-pick--multi {
  grid-template-columns: var(--bz-icon-lg) var(--bz-target-min) minmax(0, 1fr) auto;
}

/* Multiselección: el recuadro del check a la izquierda, y la baldosa marcada se rellena (la baldosa
   marcada es la del sistema: el azul de selección y el borde en la tinta de acción). */
.gf-check {
  width: var(--bz-icon-lg);
  height: var(--bz-icon-lg);
  box-sizing: border-box;
  border: 2px solid var(--bz-outline);
  border-radius: var(--bz-radius-sm);
  display: grid;
  place-items: center;
  font-size: var(--bz-icon-sm);
  color: var(--bz-on-action);
}

.gf-pick.is-selected .gf-check {
  background: var(--bz-action);
  border-color: var(--bz-action);
}

.gf-pick-actions {
  margin-top: var(--bz-space-5);
  display: flex;
  justify-content: flex-end;
}

.gf-pick-actions .bz-button {
  flex: 0 1 340px;
}

/* El campo de texto propio: grande, con el mismo lenguaje que las baldosas. Su autofoco es lo
   que abre el teclado de la tablet; dentro del iframe de Intercom no podríamos hacerlo. */
.gf-composer {
  width: 100%;
  box-sizing: border-box;
  padding: var(--bz-space-4) var(--bz-space-5);
  border: 1px solid var(--bz-outline);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  color: var(--bz-ink);
  font-family: inherit;
  font-size: var(--bz-text-xl);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-xl);
  resize: none;
}

.gf-composer::placeholder { color: var(--bz-ink-2); }

/* El paso de escribir: volver, el campo y enviar en una sola fila, y la tarjeta pegada ARRIBA del
   velo. El teclado tapa la pantalla desde abajo (en la Honor con la barra de Chrome solo quedan
   174 px visibles), así que la tarjeta centrada dejaba el campo y el botón debajo del teclado. */
.gf-card[data-step="compose"] {
  align-self: start;
  /* A la derecha, el hueco del ✕ (un 48 a 16 del borde), que es absoluto y si no pisaría Enviar. */
  padding: var(--bz-space-3) calc(var(--bz-target-min) + var(--bz-space-4) + var(--bz-space-3)) var(--bz-space-3) var(--bz-space-4);
}

.gf-compose {
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
}

.gf-compose .gf-back { flex: none; }

.gf-compose .gf-composer {
  flex: 1 1 auto;
  min-width: 0;
  padding: var(--bz-space-3) var(--bz-space-4);
}

.gf-compose .gf-send { flex: none; }

.gf-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* Recibe el foco en el toque de «Otra cosa» para que suba el teclado; no se ve ni se toca.
   A 16 px (el md): con menos, iOS hace zoom al enfocarlo. */
.gf-keyboard-proxy {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  font-size: var(--bz-text-md);
  pointer-events: none;
}

/* The order's code: the composer's field, on one line. PrimeVue's InputText brings a hairline
   shadow of its own; everything else it sets, the composer's rules already override. */
.gf-code { box-shadow: none; }

.gf-composer:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

/* ---- centred steps: working / confirm / unavailable ---- */
.gf-step--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--bz-space-6) 0 var(--bz-space-3);
}

/* The line over the question: a dialog's eyebrow (BzDialog's), 14 in the second ink. */
.gf-kicker {
  margin: 0 0 var(--bz-space-2);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
}

.gf-title--question {
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
}

/* The one fact under the question: a tag of the system, in norm 11's warn, at a tag's 14 at every width
   (it was 16 on the widest tablets only). It can be a sentence, so it wraps. */
.gf-evidence {
  margin: var(--bz-space-6) 0 0;
  padding: var(--bz-space-2) var(--bz-space-4);
  white-space: normal;
  text-align: left;
}

.gf-answers {
  width: 100%;
  margin-top: var(--bz-space-7);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bz-space-4);
}

/* The system's buttons of 72 (norm 9's primary), two in a row and as wide as a thumb needs. */
.gf-answers .bz-button {
  flex: 1 1 280px;
  max-width: 440px;
}

.gf-wait-icon {
  margin-bottom: var(--bz-space-4);
  font-size: var(--bz-icon-lg);
  color: var(--bz-action-ink);
}

.gf-unavailable-icon {
  width: var(--bz-target-primary);
  height: var(--bz-target-primary);
  margin-bottom: var(--bz-space-4);
  border-radius: var(--bz-radius-lg);
  display: grid;
  place-items: center;
  font-size: var(--bz-icon-lg);
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
}

/* ---- back / close ---- */
.gf-back { flex: 0 0 auto; }

.gf-back--inline.bz-button { margin-top: var(--bz-space-5); }

.gf-close.bz-button {
  position: absolute;
  top: var(--bz-space-4);
  right: var(--bz-space-4);
}

/* ---- a fetched pick list: loading / failed / empty / older ones ---- */
.gf-pick-state {
  min-height: 240px;
  padding: var(--bz-space-3) 0 var(--bz-space-2);
}

.gf-pick-state .gf-answers { margin-top: var(--bz-space-5); }

.gf-pick-empty {
  grid-column: 1 / -1;
  padding-block: var(--bz-space-5);
}

/* A fetched page of eight: two columns keep it to four rows, which is what fits in the 614 px of
   the pass tablet without scrolling (one column needed eight rows and a gloved scroll). Two
   classes, so it outranks the one-column rule of the ≤1199 px block below. */
.gf-grid--pick.gf-grid--pick-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }

@media screen and (max-width: 899px) {
  .gf-grid--pick.gf-grid--pick-2 { grid-template-columns: minmax(0, 1fr); }
}

/* On a phone the state goes under the code instead of beside it: side by side, a long tag
   squeezed the code to "2…", and the code is what the kitchen matches against the ticket. */
@media screen and (max-width: 640px) {
  .gf-grid--pick-2 .gf-pick { grid-template-columns: var(--bz-target-min) minmax(0, 1fr); row-gap: var(--bz-space-2); }
  .gf-grid--pick-2 .gf-pick-icon { grid-row: 1 / span 2; }
  .gf-grid--pick-2 .gf-badge { grid-column: 2; justify-self: start; }
}

/* Two exits in one row: "older ones" and "not in the list". */
.gf-option.is-free.is-half { grid-column: auto; }

/* ---- the VIEW step: the whole screen, the photo takes every pixel the bar and the link leave ---- */
/* The card is the screen: a fixed height, not max-height, because the photo is sized by what is left
   of it, so it has to have a height for the flex column to hand out. */
.gf-card[data-step="view"] {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: var(--bz-space-4) var(--bz-gutter);
  border: 0;
  border-radius: 0;
  background: var(--bz-bg);
  box-shadow: none;
}

.gf-step--view {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Two classes on purpose: the responsive blocks further down reset `.gf-head` margins with the
   same single-class weight, and would give the photo's pixels back to empty space. */
.gf-head.gf-head--view {
  margin-bottom: var(--bz-space-4);
}

.gf-view-head {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
}

/* El código al mismo peso que en su fila de la lista (.gf-pick-code): el tope del sistema, 700. */
.gf-view-code {
  font-weight: var(--bz-weight-strong);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gf-view-sub {
  margin: 0;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  font-weight: var(--bz-weight-body);
  line-height: var(--bz-leading-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gf-view-foot {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  margin-top: var(--bz-space-2);
}

@media screen and (max-height: 700px) {
  .gf-card[data-step="view"] { padding: var(--bz-space-3) var(--bz-gutter) var(--bz-space-2); }
  .gf-head.gf-head--view { margin-bottom: var(--bz-space-3); }
  .gf-pick-state { min-height: 200px; }
}

@media screen and (max-width: 640px) {
  .gf-view-head .gf-badge { display: none; }
}

/* ---- motion: opacity + transform only, and none under reduced motion ---- */
.gf-fade-leave-active { pointer-events: none; }

@media (prefers-reduced-motion: no-preference) {
  .gf-fade-enter-active,
  .gf-fade-leave-active {
    transition: opacity 160ms ease-out;
  }

  .gf-fade-enter-active .gf-card,
  .gf-fade-leave-active .gf-card {
    transition: transform 200ms var(--bz-ease-out), opacity 160ms ease-out;
  }

  .gf-step-enter-active,
  .gf-step-leave-active {
    transition: opacity 120ms ease-out, transform 120ms ease-out;
  }

  .gf-step-enter-from { transform: translateX(10px); }
  .gf-step-leave-to   { transform: translateX(-10px); }

  .gf-fade-enter-from .gf-card,
  .gf-fade-leave-to .gf-card {
    transform: translateY(16px) scale(0.98);
  }
}

.gf-fade-enter-from,
.gf-fade-leave-to { opacity: 0; }

.gf-fade-enter-from .gf-card,
.gf-fade-leave-to .gf-card { opacity: 0; }

.gf-step-enter-from,
.gf-step-leave-to { opacity: 0; }

/* ---- responsive ---- */
@media screen and (max-width: 1199px) {
  .gf-card { padding: var(--bz-space-6); }
  .gf-head { margin: 0 calc(var(--bz-target-min) + var(--bz-space-2)) var(--bz-space-6) 0; }
  .gf-grid { gap: var(--bz-space-3); }
  .gf-grid--pick { grid-template-columns: 1fr; }
  .gf-option.is-free { padding: var(--bz-space-3) var(--bz-space-4); }
  .gf-topic { padding: var(--bz-space-4); }
  .gf-close.bz-button { top: var(--bz-space-3); right: var(--bz-space-3); }
}

/* El eje que de verdad aprieta es el ALTO, no el ancho: la tablet del pase da 1114x614 px CSS,
   así que el aire calculado para 800 px de alto deja la séptima opción fuera de pantalla — que es
   exactamente lo que pasaba en la tablet, con la última baldosa acabando 5 px por debajo del
   borde. Por debajo de 700 px de alto todo el espaciado vertical se comprime, sin tocar el cuerpo
   de letra ni el objetivo táctil: se recorta aire, no legibilidad. Con estos números el modal
   ocupa lo justo de los 578 px útiles de esa tablet, así que las siete opciones entran de un
   vistazo y sobra margen. */
@media screen and (max-height: 700px) {
  .gf-overlay {
    padding: var(--bz-space-3);
    padding: max(var(--bz-space-3), env(safe-area-inset-top)) max(var(--bz-space-3), env(safe-area-inset-right))
             max(var(--bz-space-3), env(safe-area-inset-bottom)) max(var(--bz-space-3), env(safe-area-inset-left));
  }
  .gf-overlay.is-view {
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
  .gf-card { padding: var(--bz-space-5) var(--bz-space-6) var(--bz-space-6); }
  .gf-head { margin: 0 calc(var(--bz-target-min) + var(--bz-space-2)) var(--bz-space-5) 0; }
  .gf-grid { gap: var(--bz-space-3); }
  .gf-option.is-free { min-height: var(--bz-target-primary); padding: var(--bz-space-3) var(--bz-space-4); margin-top: var(--bz-space-2); }
  .gf-topic { gap: var(--bz-space-3); padding: var(--bz-space-3) var(--bz-space-4) var(--bz-space-4); }
  /* Los pasos de una sola pregunta también: aquí el aire era generoso porque había sitio. */
  .gf-step--center { padding: var(--bz-space-3) 0 var(--bz-space-2); }
  .gf-answers { margin-top: var(--bz-space-5); }
  .gf-evidence { margin-top: var(--bz-space-4); }
  .gf-wait-icon { margin-bottom: var(--bz-space-3); }
  .gf-unavailable-icon { margin-bottom: var(--bz-space-3); }
  .gf-pick { min-height: var(--bz-target-primary); }
}

@media screen and (max-width: 1023px) {
  .gf-grid--topics .gf-topic { grid-column: span 3; }
}

/* The phone: one column, and the mark beside the words — six stacked tiles would be a scroll. */
@media screen and (max-width: 640px) {
  .gf-grid { grid-template-columns: 1fr; }
  .gf-grid--topics .gf-topic,
  .gf-grid--topics .gf-option.is-free.is-half { grid-column: 1 / -1; }
  .gf-topic { flex-direction: row; align-items: center; padding: var(--bz-space-3) var(--bz-space-4); }
  .gf-title { font-size: var(--bz-text-xl); line-height: var(--bz-leading-xl); letter-spacing: var(--bz-tracking-xl); }
  .gf-head--sub { flex-wrap: wrap; row-gap: var(--bz-space-1); }
  /* En un móvil la fila de escribir no cabe: el campo arriba a lo ancho, volver y enviar debajo. */
  .gf-compose { flex-wrap: wrap; }
  .gf-compose .gf-composer { flex-basis: 100%; order: -1; }
  .gf-compose .gf-send { flex: 1 1 auto; }
}
</style>
